import { Box, Group, Stack } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useMutation } from "urql";
import { useImmerReducer } from "use-immer";
import AiPersonalityForm, { type IFormValues } from "../AiPersonalityForm";
import { type AiPersonalityEditMutation, type AiPersonalityEditMutationVariables, editMutation } from "../queries";
import AddThreadButton from "./AddThreadButton";
import CheckpointsList, { type ICheckpoint } from "./CheckpointsList";
import ImportButton from "./ImportButton";
import type { AiPersonalityQuery } from "./queries";
import SuggestImprovement from "./SuggestImprovement";
import classes from "./Tune.module.css";
import TuneThread from "./TuneThread";
import type { ThreadSuggestionData } from "./types";
import useTest from "./useTest";

interface IProps {
    aiPersonality: Exclude<AiPersonalityQuery["aiPersonality"], null>;
}

type ICheckpointState = {
    checkpoints: ICheckpoint[];
    pendingCheckpointId: string | null;
    formInstanceKey: number;
};

type ICheckpointAction =
    | { type: "ADD_MANUAL"; values: IFormValues }
    | { type: "ADD_AUTO"; values: IFormValues }
    | { type: "UPDATE_PENDING_SCORE"; score: number | null }
    | { type: "CLEAR_PENDING" }
    | { type: "INCREMENT_FORM_KEY" };

const initialCheckpointState: ICheckpointState = {
    checkpoints: [],
    pendingCheckpointId: null,
    formInstanceKey: 0,
};

function checkpointReducer(draft: ICheckpointState, action: ICheckpointAction) {
    switch (action.type) {
        case "ADD_MANUAL": {
            const cp: ICheckpoint = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                createdAt: Date.now(),
                values: structuredClone(action.values),
                source: "manual",
            };
            draft.checkpoints.unshift(cp);
            break;
        }
        case "ADD_AUTO": {
            const cp: ICheckpoint = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                createdAt: Date.now(),
                values: structuredClone(action.values),
                source: "form-test",
            };
            draft.checkpoints.unshift(cp);
            draft.pendingCheckpointId = cp.id;
            break;
        }
        case "UPDATE_PENDING_SCORE": {
            if (!draft.pendingCheckpointId) {
                break;
            }
            const idx = draft.checkpoints.findIndex((c) => c.id === draft.pendingCheckpointId);
            if (idx !== -1) {
                draft.checkpoints[idx].testScore = action.score;
            }
            break;
        }
        case "CLEAR_PENDING": {
            draft.pendingCheckpointId = null;
            break;
        }
        case "INCREMENT_FORM_KEY": {
            draft.formInstanceKey += 1;
            break;
        }
    }
}

function Tune({ aiPersonality }: IProps): JSX.Element {
    const [editResult, executeEdit] = useMutation<AiPersonalityEditMutation, AiPersonalityEditMutationVariables>(
        editMutation,
    );
    const [testLoading, handleTest, testState, testScore, clearResult] = useTest(aiPersonality);
    const [currentFormValues, setCurrentFormValues] = useState<IFormValues>({
        name: aiPersonality.name,
        externalName: aiPersonality.externalName || "",
        systemPrompt: aiPersonality.systemPrompt,
        temperature: aiPersonality.temperature,
        includeRetrieval: aiPersonality.includeRetrieval,
        includeBrainstorm: aiPersonality.includeBrainstorm,
        epsilon: aiPersonality.epsilon,
        searchPrompt: aiPersonality.searchPrompt || "",
        tags: aiPersonality.tags,
        languages: aiPersonality.languages,
        model: aiPersonality.model,
        useCache: aiPersonality.useCache,
        showInChat: false,
        reasoningEffort: aiPersonality.reasoningEffort,
        verbosity: aiPersonality.verbosity,
        useResponseFormat: !!aiPersonality.responseFormat,
        responseFormat: aiPersonality.responseFormat || "",
        maxIterations: aiPersonality.maxIterations,
    });

    // --- Checkpoints state (useImmerReducer) ---
    const [cpState, dispatch] = useImmerReducer(checkpointReducer, initialCheckpointState);

    const handleSaveCheckpoint = () => {
        dispatch({ type: "ADD_MANUAL", values: currentFormValues });
    };

    // Wrap form-level test to auto-create checkpoint and later append score
    const handleFormTest = async (values: IFormValues) => {
        dispatch({ type: "ADD_AUTO", values });
        await handleTest(values);
    };

    // When overall testing finishes AND we have a pending auto checkpoint, update its score
    useEffect(() => {
        if (!testLoading && cpState.pendingCheckpointId) {
            dispatch({ type: "UPDATE_PENDING_SCORE", score: testScore ?? null });
            dispatch({ type: "CLEAR_PENDING" });
        }
    }, [testLoading, testScore, cpState.pendingCheckpointId, dispatch]);

    const handleSave = async (values: IFormValues) => {
        const result = await executeEdit({
            data: {
                id: aiPersonality.id,
                name: values.name,
                externalName: values.externalName || null,
                systemPrompt: values.systemPrompt,
                temperature: values.temperature,
                includeRetrieval: values.includeRetrieval,
                includeBrainstorm: values.includeBrainstorm,
                epsilon: values.epsilon,
                searchPrompt: values.includeRetrieval ? values.searchPrompt : null,
                tags: values.tags,
                languages: values.languages,
                model: values.model,
                useCache: values.useCache,
                showInChat: aiPersonality.showInChat,
                reasoningEffort: aiPersonality.reasoningEffort,
                verbosity: values.verbosity,
                responseFormat: values.useResponseFormat && !values.includeRetrieval ? values.responseFormat : null,
                maxIterations: values.maxIterations,
            },
        });
        if (result.error) {
            notifications.show({
                title: "Failed to save AI personality",
                message: result.error.message,
            });
        } else {
            const aiPersonality = result.data!.aiPersonalityEdit;
            notifications.show({
                title: "AI personality saved",
                message: `AI personality ${aiPersonality.name} updated`,
            });
        }
    };

    const restoreCheckpoint = (cp: ICheckpoint) => {
        setCurrentFormValues(structuredClone(cp.values));
        dispatch({ type: "INCREMENT_FORM_KEY" }); // force remount to apply new initialValues
    };

    const suggestionThreads: ThreadSuggestionData[] = aiPersonality.tuneThreads.edges.flatMap(({ node }) => {
        const ts = testState[node.id];
        if (ts && ts.status === "SUCCESS" && ts.items.length > 0) {
            const last = ts.items[ts.items.length - 1];
            return [
                {
                    tuneThreadId: node.id,
                    answer: last.content,
                    llmJudgeScore: ts.judgement ? ts.judgement.score : null,
                    llmJudgeExplanation: ts.judgement ? ts.judgement.reasoning : null,
                },
            ];
        }
        return [];
    });

    return (
        <Stack py="md">
            <Group>
                <AddThreadButton aiPersonality={aiPersonality} />
                <ImportButton aiPersonality={aiPersonality} />
            </Group>
            <Box className={classes.scrollTainer}>
                <Box className={classes.scrollRow}>
                    {/* Left column: form + checkpoints */}
                    <Stack key={cpState.formInstanceKey}>
                        <AiPersonalityForm
                            handleSave={handleSave}
                            handleTest={handleFormTest}
                            onFormChange={setCurrentFormValues}
                            testScore={testScore}
                            initialValues={currentFormValues}
                            fetching={editResult.fetching}
                            testFetching={testLoading}
                            hideShowInChat
                        />
                        <SuggestImprovement
                            currentInstructions={currentFormValues.systemPrompt}
                            threads={suggestionThreads}
                            onApplied={(newPrompt) => {
                                setCurrentFormValues((prev) => ({ ...prev, systemPrompt: newPrompt }));
                                dispatch({ type: "INCREMENT_FORM_KEY" });
                            }}
                        />
                        <CheckpointsList
                            checkpoints={cpState.checkpoints}
                            onSaveCheckpoint={handleSaveCheckpoint}
                            onRestore={restoreCheckpoint}
                        />
                    </Stack>

                    {/* Other columns: tune threads */}
                    {aiPersonality.tuneThreads.edges.map(({ node }) => (
                        <TuneThread
                            tuneThread={node}
                            key={node.id}
                            testState={testState[node.id]}
                            aiFormValues={currentFormValues}
                            // Important: pass raw handleTest so thread-level tests do NOT create checkpoints
                            handleTest={handleTest}
                            handleClear={clearResult}
                        />
                    ))}
                </Box>
            </Box>
        </Stack>
    );
}

export default Tune;
